import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, XCircle, Bell, BellOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CDN_URL } from '@/config/cdn';

/**
 * Release Notes Unsubscribe Page
 *
 * Accessible without authentication via signed token from email.
 * Shows confirmation dialog before unsubscribing to prevent accidental
 * unsubscribes from automated security scanners following links.
 */
const ReleaseNotesUnsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading, invalid, already_unsubscribed, confirm, success, error
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  // Verify the token on mount
  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMessage('No unsubscribe token provided');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/release-notes/unsubscribe?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();

        if (!response.ok) {
          setStatus('invalid');
          setErrorMessage(data.detail || 'Invalid unsubscribe link');
          return;
        }

        if (data.already_unsubscribed) {
          setStatus('already_unsubscribed');
          setUserEmail(data.email);
        } else {
          setStatus('confirm');
          setUserEmail(data.email);
          setUserName(data.name || '');
        }
      } catch (error) {
        setStatus('error');
        setErrorMessage('Unable to verify unsubscribe link. Please try again later.');
      }
    };

    verifyToken();
  }, [token]);

  const handleUnsubscribe = async () => {
    setProcessing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/release-notes/unsubscribe?token=${encodeURIComponent(token)}`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setErrorMessage(data.detail || 'Failed to unsubscribe');
        return;
      }

      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage('Unable to complete unsubscribe. Please try again later.');
    } finally {
      setProcessing(false);
    }
  };

  const handleGoToAdmin = () => {
    window.location.href = '/';
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Verifying unsubscribe link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <img
              src={`${CDN_URL}/images/owaza-logo.jpg`}
              alt="Owaza Martial Arts"
              className="h-12 mx-auto"
            />
          </div>
          <CardTitle className="text-xl">Weekly Release Notes</CardTitle>
          <CardDescription>Email Preferences</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Invalid token */}
          {status === 'invalid' && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Invalid Link</AlertTitle>
              <AlertDescription>
                {errorMessage || 'This unsubscribe link is invalid or has expired.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Error state */}
          {status === 'error' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Already unsubscribed */}
          {status === 'already_unsubscribed' && (
            <div className="text-center space-y-4">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <BellOff className="h-8 w-8 text-gray-500 dark:text-gray-400" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Already Unsubscribed</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  <strong>{userEmail}</strong> is already unsubscribed from weekly release notes.
                </p>
              </div>
              <Button onClick={handleGoToAdmin} className="w-full">
                Go to Admin Portal
              </Button>
            </div>
          )}

          {/* Confirmation required */}
          {status === 'confirm' && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="bg-amber-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Bell className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="font-medium text-lg">Unsubscribe from Updates?</h3>
                <p className="text-gray-600 mt-2">
                  {userName ? `${userName}, you` : 'You'} will no longer receive weekly website update emails at:
                </p>
                <p className="font-medium mt-1">{userEmail}</p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  These emails summarize new features, fixes, and improvements to your Owaza website.
                  You can re-subscribe anytime from the Admin Portal settings.
                </p>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={handleUnsubscribe}
                  disabled={processing}
                  variant="destructive"
                  className="w-full"
                >
                  {processing ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Processing...
                    </>
                  ) : (
                    'Yes, Unsubscribe Me'
                  )}
                </Button>
                <Button
                  onClick={handleGoToAdmin}
                  variant="outline"
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Successfully Unsubscribed</h3>
                <p className="text-gray-600 mt-2">
                  <strong>{userEmail}</strong> has been unsubscribed from weekly release notes.
                </p>
                <p className="text-sm text-gray-500 mt-3">
                  You can re-subscribe anytime from the Admin Portal settings.
                </p>
              </div>
              <Button onClick={handleGoToAdmin} className="w-full">
                Go to Admin Portal
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReleaseNotesUnsubscribe;
